import { useState } from 'react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { FileSpreadsheet, Lock, Loader2, ShieldCheck, Eye, EyeOff, Check, Download, Info } from 'lucide-react'

// What the file holds, so nobody exports blind. Kept in step with COLUMNS in routers/personal_export.py.
const CONTENTS = [
  { title: 'Identity and contact', items: 'Name, email, consultant code, status, phone, LinkedIn, nationality, date of birth, home address, ID document type and number' },
  { title: 'Bank details', items: 'Account holder, bank name and address, SWIFT / BIC, account number, IBAN, mailing address' },
  { title: 'About this file', items: 'A second sheet: who exported it, when, the reason given and how many people' },
]

const INPUT = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'

/**
 * Decrypted personal data as a password-protected Excel file. For Admins and anyone an Admin
 * granted "Export personal data" on People & Access. The server holds the same rule
 * (routers/personal_export.py); this page only spares everyone else a door that does not open.
 */
export default function PersonalDataExport() {
  const { user } = useAuth()
  const allowed = user?.role === 'Admin' || user?.is_super_admin
    || (user?.capabilities || []).includes('export_personal_data')
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  if (!allowed) {
    return (
      <div className="p-6">
        <div className="max-w-xl mx-auto mt-10 bg-white border border-gray-200 rounded-xl shadow-sm p-8 text-center">
          <Lock className="w-8 h-8 mx-auto text-gray-400" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900">Personal Data Export</h1>
          <p className="mt-1 text-sm text-gray-500">You do not have the “Export personal data” permission. An Admin can give it on People &amp; Access.</p>
        </div>
      </div>
    )
  }

  const reasonLength = reason.trim().length
  const longEnough = password.length >= 12
  const matches = password.length > 0 && password === again
  const ready = reasonLength >= 10 && longEnough && matches

  const run = async (e) => {
    e.preventDefault()
    setBusy(true); setError(null); setDone(false)
    try {
      const r = await api.post('/admin/personal-data/export', { reason: reason.trim(), password }, { responseType: 'blob' })
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'ProTrack-personal-data.xlsx'
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = name
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      setPassword(''); setAgain(''); setDone(true)
    } catch (err) {
      // The error body arrives as a blob too: read it to show the server's own reason.
      let msg = 'Export failed'
      try { msg = JSON.parse(await err.response.data.text()).detail || msg } catch { /* keep the default */ }
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-linear-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white flex items-start gap-4">
          <div className="p-3 bg-white/15 rounded-lg shrink-0"><FileSpreadsheet className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold">Personal Data Export</h1>
            <p className="mt-1 text-sm opacity-90">Every consultant’s personal and bank details, decrypted, in an Excel file locked with your password.</p>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        {done && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 text-sm flex items-center gap-2">
            <Check className="w-4 h-4 shrink-0" /> Downloaded. Open it with your password, and delete it when you have finished with it.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3 items-start">
          <form onSubmit={run} className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-6 space-y-7">
              <section>
                <Step n="1" title="Why do you need this export?" />
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} className={`mt-3 ${INPUT}`}
                  placeholder="e.g. Annual check of bank details before the payment run" />
                <div className="mt-1.5 flex items-center justify-between text-xs">
                  <span className="text-gray-400">Kept in the audit log with your name and the time.</span>
                  <span className={reasonLength >= 10 ? 'text-emerald-600' : 'text-gray-400'}>{Math.min(reasonLength, 10)} / 10 characters</span>
                </div>
              </section>

              <section>
                <Step n="2" title="Lock the file with a password" />
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">File password</span>
                    <div className="relative mt-1">
                      <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password}
                        onChange={e => setPassword(e.target.value)} className={`${INPUT} pr-10`} />
                      <button type="button" onClick={() => setShowPassword(s => !s)}
                        className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">Repeat the password</span>
                    <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={again}
                      onChange={e => setAgain(e.target.value)} className={`mt-1 ${INPUT}`} />
                  </label>
                </div>
                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <Rule ok={longEnough}>At least 12 characters</Rule>
                  <Rule ok={matches}>Both passwords match</Rule>
                </ul>
              </section>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 shrink-0" /> Downloaded once. ProTrack keeps neither the file nor the password.
              </p>
              <button type="submit" disabled={!ready || busy}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export to Excel
              </button>
            </div>
          </form>

          <aside className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
              <p className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Before you export</p>
              <ul className="mt-3 space-y-2 list-disc pl-5">
                <li>Your name, the time, your reason and the number of people are recorded.</li>
                <li>Send the password separately from the file, never in the same email.</li>
                <li>Delete the file when you have finished with it.</li>
              </ul>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <p className="text-sm font-semibold text-gray-900">What the file contains</p>
              <dl className="mt-3 space-y-3">
                {CONTENTS.map(c => (
                  <div key={c.title}>
                    <dt className="text-sm font-medium text-gray-800">{c.title}</dt>
                    <dd className="mt-0.5 text-xs text-gray-500 leading-relaxed">{c.items}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function Step({ n, title }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold flex items-center justify-center">{n}</span>
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    </div>
  )
}

function Rule({ ok, children }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded-full border border-current" />}
      {children}
    </li>
  )
}
